import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import {
    DaTable,
    DaTableBody,
    DaTableCell,
    DaTableHeadCell,
    DaTableHead,
    DaTableRow,
    EyeIcon,
    Text,
    OutIcon,
    InternalGrid,
} from '@paygreen/paygreen-ui';
import { IsBigDesktop, truncateTooLongName } from '../../../utils/tools';
import { StatusDisplayTable, HideAndCopy } from '../../../components';
import { ClimateKitBadge, CharityKitBadge } from '../../../assets/logos';
import {
    activationStatusOptions,
    accountStatusOptions,
    getNameIdAccountTeam,
    actionCellboxProps,
    actionIconProps,
    widthCellboxProps,
} from '../shared';
import { DisableBadge } from './ActiveBadge.styled';

const AdminUsersDaTable = ({
    t,
    data,
    isLoading,
    teamMembers,
    getTableProps,
    headerGroups,
    getTableBodyProps,
    page,
    prepareRow,
}) => {
    const isBigDesktop = IsBigDesktop();

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems="stretch"
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <DaTable
                {...getTableProps()}
                blockWidth="none"
                isLoading={isLoading}
                loadingRowNumber={30}
                loadingMainCellNumber={3}
            >
                {headerGroups.map((headerGroup) => (
                    <DaTableHead
                        blockTitle={t('table.title')}
                        {...headerGroup.getHeaderGroupProps()}
                    >
                        {headerGroup.headers.map((column, index) => (
                            <DaTableHeadCell
                                key={index}
                                label={column.render('Header')}
                            >
                                {![2, 8].includes(index)
                                    ? column.render('Filter')
                                    : null}
                            </DaTableHeadCell>
                        ))}
                    </DaTableHead>
                ))}

                <DaTableBody {...getTableBodyProps()}>
                    {!isLoading && data.length && page.length ? (
                        page.map((row, index) => {
                            prepareRow(row);
                            const {
                                organisation,
                                user,
                                createdAt,
                                spaces,
                                accountEnabled,
                                manager,
                                commercial,
                                customerSuccess,
                            } = row.values;

                            return (
                                <DaTableRow {...row.getRowProps()} key={index}>
                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.organisationName',
                                        )}
                                        title={organisation[0]}
                                        {...widthCellboxProps(
                                            isBigDesktop,
                                            'sm',
                                        )}
                                    >
                                        {truncateTooLongName(
                                            organisation[0],
                                            80,
                                        )}

                                        {organisation[1] && (
                                            <a
                                                href={organisation[1]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hideOnMobile"
                                            >
                                                <OutIcon
                                                    marginLeft="sm"
                                                    title={organisation[1]}
                                                    {...actionIconProps(true)}
                                                />
                                            </a>
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t('admin.users.table.user')}
                                        isMain={false}
                                        {...widthCellboxProps(
                                            isBigDesktop,
                                            'lg',
                                        )}
                                    >
                                        {user[0]} {user[1]}
                                        <InternalGrid
                                            displayType="grid"
                                            gridTemplateColumns="auto auto"
                                        >
                                            {user[2] && (
                                                <HideAndCopy
                                                    title={
                                                        t(
                                                            'admin.users.table.copyPhone',
                                                        ) + user[2]
                                                    }
                                                    hasHiddenText={false}
                                                    icon="PhoneIcon"
                                                    text={user[2]}
                                                />
                                            )}

                                            {user[3] && (
                                                <HideAndCopy
                                                    title={
                                                        t(
                                                            'admin.users.table.copyEmail',
                                                        ) + user[3]
                                                    }
                                                    hasHiddenText={false}
                                                    icon="LetterIcon"
                                                    text={user[3]}
                                                />
                                            )}
                                        </InternalGrid>
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.creationDate',
                                        )}
                                        isMain={false}
                                    >
                                        {createdAt}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.activationDate',
                                        )}
                                        isMain={false}
                                    >
                                        <StatusDisplayTable
                                            status={accountEnabled}
                                            statusOptions={
                                                activationStatusOptions
                                            }
                                            i18TextBase="admin.users.table.activationStatusFilter"
                                        />
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.activatedSpaces',
                                        )}
                                        isMain={false}
                                    >
                                        <InternalGrid
                                            displayType="grid"
                                            gridGap="sm"
                                            gridTemplateColumns="auto auto"
                                        >
                                            {!!spaces[0] && (
                                                <DisableBadge
                                                    isActive={
                                                        spaces[2] !== 'SIGNED'
                                                    }
                                                >
                                                    <ClimateKitBadge
                                                        hasArrow={false}
                                                        isShorten={true}
                                                        title={
                                                            spaces[2] !==
                                                            'SIGNED'
                                                                ? t(
                                                                      'admin.users.table.spacesStatusFilter.CLIMATE.title',
                                                                  )
                                                                : t(
                                                                      'admin.users.table.spacesStatusFilter.CLIMATE.titleSigned',
                                                                  )
                                                        }
                                                    />
                                                </DisableBadge>
                                            )}

                                            {!!spaces[1] && (
                                                <DisableBadge
                                                    isActive={
                                                        spaces[3] !== 'SIGNED'
                                                    }
                                                >
                                                    <CharityKitBadge
                                                        isShorten={true}
                                                        title={
                                                            spaces[3] !==
                                                            'SIGNED'
                                                                ? t(
                                                                      'admin.users.table.spacesStatusFilter.CHARITY.title',
                                                                  )
                                                                : t(
                                                                      'admin.users.table.spacesStatusFilter.CHARITY.titleSigned',
                                                                  )
                                                        }
                                                    />
                                                </DisableBadge>
                                            )}
                                        </InternalGrid>
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.accountManager',
                                        )}
                                        isMain={false}
                                    >
                                        {manager ? (
                                            getNameIdAccountTeam(
                                                manager,
                                                teamMembers,
                                            )
                                        ) : (
                                            <StatusDisplayTable
                                                status="EMPTY"
                                                statusOptions={
                                                    accountStatusOptions
                                                }
                                                i18TextBase="admin.users.table.accountStatusFilter"
                                            />
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t(
                                            'admin.users.table.commercial',
                                        )}
                                        isMain={false}
                                    >
                                        {commercial ? (
                                            getNameIdAccountTeam(
                                                commercial,
                                                teamMembers,
                                            )
                                        ) : (
                                            <StatusDisplayTable
                                                status="EMPTY"
                                                statusOptions={
                                                    accountStatusOptions
                                                }
                                                i18TextBase="admin.users.table.commercialStatusFilter"
                                            />
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label={t('admin.users.table.csm')}
                                        isMain={false}
                                    >
                                        {customerSuccess ? (
                                            getNameIdAccountTeam(
                                                customerSuccess,
                                                teamMembers,
                                            )
                                        ) : (
                                            <StatusDisplayTable
                                                status="EMPTY"
                                                statusOptions={
                                                    accountStatusOptions
                                                }
                                                i18TextBase="admin.users.table.commercialStatusFilter"
                                            />
                                        )}
                                    </DaTableCell>

                                    <DaTableCell
                                        label="Actions"
                                        {...actionCellboxProps(isBigDesktop)}
                                    >
                                        <RouterLink
                                            to={{
                                                pathname: `/admin/users/${organisation[2]}`,
                                            }}
                                        >
                                            <EyeIcon
                                                title={t(
                                                    'admin.users.table.titleDetails',
                                                )}
                                                {...actionIconProps(true)}
                                            />
                                        </RouterLink>
                                    </DaTableCell>
                                </DaTableRow>
                            );
                        })
                    ) : (
                        <Text
                            align="center"
                            marginTop="xl"
                            marginBottom="xl"
                            textSize="sm"
                        >
                            {t('noDataFound')}
                        </Text>
                    )}
                </DaTableBody>
            </DaTable>
        </InternalGrid>
    );
};

AdminUsersDaTable.propTypes = {
    data: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    teamMembers: PropTypes.array.isRequired,
    getTableProps: PropTypes.func.isRequired,
    headerGroups: PropTypes.array.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    page: PropTypes.array.isRequired,
    prepareRow: PropTypes.func.isRequired,
};

AdminUsersDaTable.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(AdminUsersDaTable);
